import { useId, type ReactNode } from "react";
import clsx from "clsx";
import type { FieldLabelVariant } from "./Field.js";
import styles from "./Field.module.scss";

export interface FieldShellProps {
  /** Mono uppercase label. */
  label: string;
  /** Keep the label for screen readers only — a row that already names the control visibly. */
  hideLabel?: boolean;
  /** ADDITIVE error state: danger border on the control + a danger-ink caption. */
  error?: string;
  hint?: ReactNode;
  /** See {@link FieldLabelVariant}. */
  labelVariant?: FieldLabelVariant;
  /** Guidance between the label and the control. */
  subLabel?: ReactNode;
  className?: string;
  /**
   * Renders the control. Spread `props` onto it and add `controlClass` to inherit the shared
   * treatment (paper fill, 1.5px border, accent focus ring, gray disabled).
   */
  children: (props: { id: string; "aria-invalid"?: true; "aria-describedby"?: string; className: string }) => ReactNode;
}

/**
 * The label + error/hint frame on its own, for a control the typed components don't cover (a combo
 * box, a segmented control, an input with an inline affix). Everything `Field` / `Textarea` /
 * `Select` give you except the element itself — so a bespoke control still gets the right label
 * treatment, wiring and error state instead of a feature re-inventing all three.
 */
export function FieldShell({ label, hideLabel, labelVariant, subLabel, error, hint, className, children }: FieldShellProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const subId = `${id}-sub`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={clsx(styles.field, error && styles.hasError, className)}>
      <label htmlFor={id} className={hideLabel ? styles.srOnly : styles.label} data-variant={labelVariant}>
        {label}
      </label>
      {subLabel && (
        <p id={subId} className={styles.subLabel}>
          {subLabel}
        </p>
      )}
      {children({
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
        className: styles.control,
      })}
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
